import { useEffect, useState } from 'react';

import Benefits from '@/components/familyPlan/Benefits';
import Faq from '@/components/familyPlan/Faq';
import Hero from '@/components/familyPlan/Hero';
import IneligibleOverlay from '@/components/familyPlan/IneligibleOverlay';
import Unlock from '@/components/familyPlan/Unlock';
import Header from '@/components/header/Header';
import Loader from '@/components/Loader';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useFamilyParams } from '@/hooks/app/useFamilyParams';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { calmLogger } from '@/utils/calmLogger';

import { FamilyPlanLandingProps, FamilyPlanParams, OnCtaClick } from './types';

export const getFamilyPlanParams = (hash?: string): FamilyPlanParams | null => {
	if (!hash) return null;

	try {
		const parsedFamilyParams: FamilyPlanParams = JSON.parse(decodeURIComponent(atob(hash)));
		return parsedFamilyParams;
	} catch (error) {
		calmLogger.error(`[getFamilyPlanParams] hash: ${hash}`, {}, error);
		return null;
	}
};

const FamilyPlanLanding = ({ isJoinFlow = false }: FamilyPlanLandingProps) => {
	const { logEvent } = useAnalytics();
	const routerPush = useRouterPush();
	const apiRequest = useApi();

	const [isIneligible, setIsIneligible] = useState(false);
	const [isFetching, setIsFetching] = useState(isJoinFlow);

	const { familyParams, isLoading, familyParamsHash } = useFamilyParams(isJoinFlow);

	const onClick: OnCtaClick = async ({ section, copy }) => {
		logEvent({
			eventName: 'Family Plan Landing : CTA : Clicked',
			eventProps: {
				section,
				copy,
				type: 'button',
			},
		});
		await routerPush(isJoinFlow ? `/family/signup/${familyParamsHash}` : '/family/signup');
	};

	const checkEligibility = async () => {
		try {
			const familyId = familyParams?.familyId;

			if (!familyId) {
				throw Error('Missing family plan ID');
			}

			const { data } = await apiRequest<{ full: boolean }>({
				method: 'GET',
				endpoint: `familyplan/${familyId}/eligibility`,
				noCache: true,
			});
			setIsIneligible(data.full);
		} catch (err) {
			setIsIneligible(false);
		} finally {
			setIsFetching(false);
		}
	};

	useEffect(
		() => {
			if (isJoinFlow && familyParams?.familyId) {
				checkEligibility().catch(error =>
					calmLogger.error('Error in FamilyPlanLanding checkEligibility', {}, error),
				);
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[familyParams?.familyId],
	);

	useEffect(
		() => {
			if (isLoading) return;

			if (isJoinFlow && familyParams === null && !isLoading) {
				routerPush('/not-found', { familyParams: null });
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[familyParams],
	);

	if (isFetching) {
		return <Loader color="gray8" />;
	}

	return (
		<>
			<Header isStatic hideMenu hideProfile />
			{isIneligible ? (
				<IneligibleOverlay adminName={familyParams?.adminName} />
			) : (
				<>
					<Hero onClick={onClick} isJoinFlow={isJoinFlow} adminName={familyParams?.adminName} />
					{!isJoinFlow && (
						<>
							<Benefits isJoinFlow={isJoinFlow} />
							<Unlock isJoinFlow={isJoinFlow} />
							<Faq onClick={onClick} isJoinFlow={isJoinFlow} />
						</>
					)}
				</>
			)}
		</>
	);
};

export default FamilyPlanLanding;
